import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import Recorder from "./Recorder";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Plus, Trash2, Wand2, Loader2, User } from "lucide-react";

function mondayOf(d = new Date()) {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const m = new Date(d);
  m.setDate(d.getDate() + diff);
  return m.toISOString().slice(0, 10);
}
const today = () => new Date().toISOString().slice(0, 10);

export default function WeeklyBuilder({ consumers, onCompiled }) {
  const [consumerId, setConsumerId] = useState("");
  const [weekStart, setWeekStart] = useState(mondayOf());
  const [draft, setDraft] = useState(null);
  const [opening, setOpening] = useState(false);
  const [entryDate, setEntryDate] = useState(today());
  const [entryText, setEntryText] = useState("");
  const [adding, setAdding] = useState(false);
  const [compiling, setCompiling] = useState(false);

  const consumer = consumers.find((c) => c.id === consumerId);

  const openWeek = async () => {
    if (!consumerId) return toast.info("Pick a consumer first.");
    setOpening(true);
    try {
      const { data } = await api.post("/weekly", {
        consumer_id: consumerId,
        consumer_name: consumer?.name || "",
        week_start: weekStart,
      });
      setDraft(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setOpening(false);
    }
  };

  const addEntry = async () => {
    if (!entryText.trim()) return toast.info("Add what happened that day.");
    setAdding(true);
    try {
      const { data } = await api.post(`/weekly/${draft.id}/entries`, {
        date: entryDate,
        text: entryText,
      });
      setDraft(data);
      setEntryText("");
      toast.success("Daily entry added.");
    } catch (e) {
      toast.error("Could not add entry.");
    } finally {
      setAdding(false);
    }
  };

  const removeEntry = async (eid) => {
    try {
      const { data } = await api.delete(`/weekly/${draft.id}/entries/${eid}`);
      setDraft(data);
    } catch {
      toast.error("Could not remove entry.");
    }
  };

  const compile = async () => {
    setCompiling(true);
    try {
      const { data } = await api.post(`/weekly/${draft.id}/generate`, { selected_goals: [] });
      onCompiled(data.structured, consumerId, weekStart, data.alignment, data.repeat);
      toast.success(data.repeat?.repeat ? "Weekly note compiled — but interventions match last week. Fix them before saving." : "Weekly note compiled — review, sign & save it.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCompiling(false);
    }
  };

  return (
    <div data-testid="weekly-builder">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
          <CalendarDays className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">
            Build a Weekly Note
          </h1>
          <p className="text-sm text-[#7A8C82]">
            Dictate a little each day. Anyone who covers the member can add their day too. Then compile it all into one weekly note.
          </p>
        </div>
      </div>

      {/* pick consumer + week */}
      <div className="bg-white border border-[#E2DFD8] rounded-2xl shadow-sm p-6">
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Consumer</Label>
            <Select value={consumerId || "none"} onValueChange={(v) => { setConsumerId(v === "none" ? "" : v); setDraft(null); }}>
              <SelectTrigger data-testid="weekly-consumer-select" className="h-11 bg-[#F9F9F6] border-[#E2DFD8]">
                <SelectValue placeholder="Select a consumer" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select a consumer</SelectItem>
                {consumers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.member_id ? ` · ${c.member_id}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Week starting (Mon)</Label>
            <Input data-testid="weekly-week-input" type="date" value={weekStart} onChange={(e) => { setWeekStart(e.target.value); setDraft(null); }} className="h-11 bg-[#F9F9F6] border-[#E2DFD8]" />
          </div>
          <Button data-testid="weekly-open-btn" onClick={openWeek} disabled={opening} className="h-11 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white">
            {opening ? <Loader2 className="w-4 h-4 animate-spin" /> : "Open this week"}
          </Button>
        </div>
      </div>

      {draft && (
        <>
          {/* add daily entry */}
          <div className="mt-6 bg-white border border-[#E2DFD8] rounded-2xl shadow-sm p-6 fade-up">
            <h3 className="font-display text-lg font-bold text-[#121814] mb-4">Add a daily entry</h3>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Date</Label>
                <Input data-testid="weekly-entry-date" type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="h-11 bg-[#F9F9F6] border-[#E2DFD8] w-[170px]" />
              </div>
              <div className="shrink-0 pt-5">
                <Recorder onTranscript={(t) => setEntryText((r) => (r ? r + " " + t : t))} />
              </div>
              <div className="flex-1 w-full space-y-1.5">
                <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">What happened that day</Label>
                <Textarea data-testid="weekly-entry-text" value={entryText} onChange={(e) => setEntryText(e.target.value)} rows={3} placeholder="e.g. Practiced budgeting for 30 minutes. He was engaged and did most of it himself." className="bg-[#F9F9F6] border-[#E2DFD8] text-sm resize-y" />
                <Button data-testid="weekly-add-entry-btn" onClick={addEntry} disabled={adding} className="mt-1 h-10 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-sm">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> Add day</>}
                </Button>
              </div>
            </div>
          </div>

          {/* entries list */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold text-[#121814]">
                This week's entries ({draft.entries?.length || 0})
              </h3>
              <Button
                data-testid="weekly-compile-btn"
                onClick={compile}
                disabled={compiling || !(draft.entries?.length)}
                className="h-11 px-6 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white shadow-[0_8px_16px_rgba(74,107,83,0.25)]"
              >
                {compiling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Compiling…</> : <><Wand2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Compile Weekly Note</>}
              </Button>
            </div>
            {!(draft.entries?.length) && (
              <p className="text-sm text-[#7A8C82] px-1">No entries yet. Add each day above — you can do it a little at a time.</p>
            )}
            <div className="space-y-2">
              {(draft.entries || []).map((e) => (
                <div key={e.id} data-testid={`weekly-entry-${e.id}`} className="group bg-white border border-[#E2DFD8] rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#4A6B53] flex items-center gap-1.5">
                        <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.5} /> {e.date}
                        <span className="text-[#7A8C82] font-normal flex items-center gap-1 ml-1">
                          <User className="w-3 h-3" strokeWidth={1.5} /> {e.staff_name}{e.staff_credentials ? `, ${e.staff_credentials}` : ""}
                        </span>
                      </p>
                      <p className="text-sm text-[#3A453F] mt-1 leading-relaxed">{e.text}</p>
                    </div>
                    <button data-testid={`weekly-delete-entry-${e.id}`} onClick={() => removeEntry(e.id)} className="opacity-0 group-hover:opacity-100 text-[#7A8C82] hover:text-[#C26D5C] transition-opacity shrink-0">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
